<!-- Loading -->
<div class="dashboard-container" *ngIf="loading">
  <div class="loading-state">
    <div class="loading-spinner"></div>
    <p>Loading what’s changed…</p>
  </div>
</div>

<!-- Error -->
<div class="dashboard-container" *ngIf="!loading && error">
  <div class="empty-panel card">
    <h2>Something went wrong</h2>
    <p>{{ error }}</p>
    <button type="button" class="btn btn--accent" (click)="load()">Try again</button>
  </div>
</div>

<!-- ===========================================================================
     Ladder 4 — no crawl pair (IB4 / IMPACT_BRIEF_PLAN.md decision 10).

     The three blocks below are the states with no movement to show. Each keeps
     its own header and its own sentence about the crawl situation, because
     "nothing captured", "one run so far" and "the diff layer refused this pair"
     are three different things to tell an operator. What they share is the
     body: the week plan composed from gap and opportunity, which does not need
     a pair to exist. That body lives in #weekPlan once.
     ======================================================================== -->

<ng-template #weekPlan>
  <!-- Every call site of this template is a NO-PAIR path — no captured runs,
       one run, or a refused pair — so the placeholder takes the first-crawl
       title the strip itself will render a moment later. The paired branch has
       its own loading section further down and keeps the period wording. -->
  <section class="impact-loading" *ngIf="firstRunPanel === 'loading'">
    <h2>What to work on first</h2>
    <p>Working out what to work on…</p>
  </section>

  <!-- No pair means no measured win or loss, and the subhead says so rather
       than letting package titles imply movement that was never measured. -->
  <app-impact-packages
    *ngIf="firstRunPanel === 'packages'"
    [packages]="brief!.packages"
    [headline]="brief!.headline"
    [readiness]="brief!.readiness"
    [mode]="brief!.mode"
    [totals]="brief!.totals"
    [domain]="briefDomain"
    [expandingPackageId]="expandingPackageId"
    [hideDecidedPages]="true"
    (expandPackage)="expandPackage($event)"
    subhead="Ranked on demand gap and opportunity. Nothing here claims a win or a loss — that needs a second crawl."
  ></app-impact-packages>

  <!-- The sentences come from IB2's `impactBriefEmptyMessage` so this panel and
       the inline one inside app-impact-packages cannot word the same state two
       ways. What IB4 adds is the affordance: the right call to action, which is
       the half that makes these states stop being dead ends. -->

  <!-- Never analysed: Setup is the honest next step. -->
  <div class="empty-panel card" *ngIf="firstRunPanel === 'no-analysis'">
    <h2>Nothing analysed yet</h2>
    <p>{{ emptyMessage }}</p>
    <div class="empty-actions">
      <a routerLink="/setup" class="btn btn--accent">Go to Setup</a>
    </div>
  </div>

  <!-- Analysed, and the queue usually has rows — none of them package-shaped.
       Sending this operator to Setup would tell them to redo finished work. -->
  <div class="empty-panel card" *ngIf="firstRunPanel === 'no-packages'">
    <h2>No week package yet</h2>
    <p>{{ emptyMessage }}</p>
    <div class="empty-actions">
      <a
        *ngIf="openQueueItems > 0"
        [routerLink]="issuesLink()"
        [queryParams]="{ section: 'planV2' }"
        class="btn btn--accent"
      >Open Issues ({{ openQueueItems }})</a>
      <a routerLink="/inventory" class="btn btn--quiet">Inventory</a>
    </div>
  </div>
</ng-template>

<!-- ===========================================================================
     Site-wide technical issues — the "fix once" section.

     Below the week plan, and its own template so all four dashboard states get
     the same section from one definition. It is deliberately NOT part of the
     brief: it reads `PageAnalysis` rather than the synthesis, so it renders on a
     first-run domain with no crawl pair, and a brief that fails to load does not
     take it with it — which is why it stays a sibling of the packages rather
     than moving inside them now that it sits underneath.

     Below rather than above: the week plan is what the operator came for, and a
     nine-row table of template defects above it pushed the actual plan under the
     fold. These rows are high-leverage but they are not this week's work — they
     are the same list every week until someone changes the theme.
     ======================================================================== -->
<!-- The ref is #siteWideSection, not #siteWideIssues: a template reference
     variable shadows component members inside the template, so a ref sharing
     the getter's name would make `[issues]` below bind to the TemplateRef. -->
<ng-template #siteWideSection>
  <app-site-wide-issues
    *ngIf="hasSiteWideIssues"
    [issues]="siteWideIssues"
    [rollup]="technicalRollupPayload"
  ></app-site-wide-issues>
</ng-template>

<!-- Escape hatches shown under a week plan that did render. -->
<ng-template #weekPlanFooter>
  <footer class="dash-footer" *ngIf="hasPackages">
    <a [routerLink]="issuesLink()" [queryParams]="{ section: 'planV2' }" class="btn btn--quiet">Issues</a>
    <a routerLink="/inventory" class="btn btn--quiet">Inventory</a>
    <a routerLink="/crawl-management" class="btn btn--quiet">Crawl history</a>
  </footer>
</ng-template>

<!-- No captured runs in workspace -->
<div class="dashboard-container" *ngIf="!loading && !error && isEmptyWorkspace">
  <header class="dash-header">
    <h1>{{ briefDomain || 'Dashboard' }}</h1>
    <p class="dash-sub" *ngIf="hasPackages">
      No crawl history yet, so nothing has moved. This is what the pages are worth doing now.
    </p>
    <!-- Held back until the brief has answered: this line contradicts a week
         plan, and flashing it before the packages land says the wrong thing. -->
    <p class="dash-sub" *ngIf="!hasPackages && firstRunPanel !== 'loading'">
      Once a domain has two crawl runs, winners and losers show up here.
    </p>
  </header>

  <ng-container *ngTemplateOutlet="weekPlan"></ng-container>

  <!-- Pre-packages copy, kept verbatim for when the brief cannot be read at all
       (network failure, or the production ACTION_PLAN_V2_ENABLED gate the
       endpoint sits behind). Degrade, don't error. -->
  <div class="empty-panel card" *ngIf="briefUnavailable">
    <h2>No crawl history yet</h2>
    <p>Capture a crawl (and a follow-up) in Setup to see which pages are winning or decaying.</p>
    <div class="empty-actions">
      <a routerLink="/setup" class="btn btn--accent">Go to Setup</a>
    </div>
  </div>

  <ng-container *ngTemplateOutlet="siteWideSection"></ng-container>

  <ng-container *ngTemplateOutlet="weekPlanFooter"></ng-container>
</div>

<!-- First run only -->
<div class="dashboard-container" *ngIf="!loading && !error && movers && isFirstRun">
  <header class="dash-header">
    <h1>{{ movers.scopeDomain }}</h1>
    <p class="dash-sub">
      <span *ngIf="period() as p">{{ p.label }}. </span>
      Winners and losers appear after the next run.
    </p>
  </header>

  <ng-container *ngTemplateOutlet="weekPlan"></ng-container>

  <div class="empty-panel card" *ngIf="briefUnavailable">
    <h2>{{ movers.headline }}</h2>
    <div class="empty-actions">
      <a routerLink="/inventory" class="btn btn--accent">Open Inventory</a>
      <a routerLink="/crawl-management" class="btn btn--quiet">Crawl history</a>
    </div>
  </div>

  <ng-container *ngTemplateOutlet="siteWideSection"></ng-container>

  <ng-container *ngTemplateOutlet="weekPlanFooter"></ng-container>
</div>

<!-- Compared pair -->
<div class="dashboard-container" *ngIf="!loading && !error && movers && hasPair">
  <header class="dash-header">
    <div class="dash-header-row">
      <div>
        <h1>{{ movers.scopeDomain }}</h1>
        <p class="dash-sub" *ngIf="period() as p">{{ p.label }}</p>
      </div>
    </div>
  </header>

  <!-- One hairline strip, not three cards. Three bordered, rounded boxes to
       carry three numbers is three times the chrome the numbers need, and it
       made the top of the page read as a different screen from everything
       below it. The value and its delta are two elements now, so the delta can
       carry the tone: they used to be one server-composed string, which meant
       "2690.7 (+335.9)" was coloured green in its entirety, including the
       total, which has no direction. -->
  <section class="metric-strip card" *ngIf="movers.siteSummary as summary">
    <div class="metric" *ngFor="let m of summaryMetrics(summary)">
      <span class="metric__k">{{ m.label }}</span>
      <span class="metric__v">{{ m.valueDisplay }}</span>
      <span class="metric__d" [ngClass]="changeTone(m.change)">{{ m.deltaDisplay }}</span>
    </div>
  </section>

  <!-- This period's work packages. Composed by IB1; rendered in received order.
       Loads after the movers (it is pinned to their resolved domain), so it holds
       its own space rather than pushing the movers down when it arrives. -->
  <section class="impact-loading" *ngIf="briefLoading">
    <h2>This week’s impact</h2>
    <p>Working out this period’s packages…</p>
  </section>

  <app-impact-packages
    *ngIf="!briefLoading && hasBrief"
    [packages]="brief!.packages"
    [headline]="brief!.headline"
    [readiness]="brief!.readiness"
    [emptyState]="brief!.emptyState"
    [mode]="brief!.mode"
    [totals]="brief!.totals"
    [domain]="movers.scopeDomain"
    [expandingPackageId]="expandingPackageId"
    [hideDecidedPages]="true"
    (expandPackage)="expandPackage($event)"
    subhead="The biggest things to work on this period, grouped into packages."
  ></app-impact-packages>

  <ng-container *ngTemplateOutlet="siteWideSection"></ng-container>

  <footer class="dash-footer">
    <a routerLink="/inventory" class="btn btn--quiet">Inventory</a>
    <a routerLink="/crawl-management" class="btn btn--quiet">Crawl history</a>
    <a routerLink="/inventory" [queryParams]="{ section: 'planV2' }" class="btn btn--quiet">Issues</a>
  </footer>
</div>

<!-- Compared refused / other states with a payload -->
<div
  class="dashboard-container"
  *ngIf="!loading && !error && movers && !hasPair && !isFirstRun && !isEmptyWorkspace"
>
  <header class="dash-header">
    <h1>{{ movers.scopeDomain || 'Dashboard' }}</h1>
    <p class="dash-sub">{{ movers.headline }}</p>
  </header>

  <!-- The pair was refused, so there is no movement to show — but the queue is
       unaffected by that refusal, and the week plan reads from the queue. -->
  <p *ngIf="movers.reason" class="reason">{{ movers.reason }}</p>

  <ng-container *ngTemplateOutlet="weekPlan"></ng-container>

  <div class="empty-panel card" *ngIf="briefUnavailable">
    <div class="empty-actions">
      <a routerLink="/crawl-management" class="btn btn--accent">Crawl history</a>
    </div>
  </div>

  <ng-container *ngTemplateOutlet="siteWideSection"></ng-container>

  <ng-container *ngTemplateOutlet="weekPlanFooter"></ng-container>
</div>
